/**
 * PublishToMediaModal — CORE-588 (screen 26) «Публикация в Медиа».
 *
 * The single surface a master uses, after a Live-сессия ends, to publish its
 * Ready recording into the «Медиа» library — with a preview and editable
 * название / описание / обложка / видимость — and later to снять с публикации
 * or удалить запись. Opened from:
 *
 *   • the завершение экран (screen 24) — «Опубликовать в Медиа» CTA;
 *   • «Мои комнаты» (screen 25) — the recording-management action on an
 *     ended session that captured a recording.
 *
 * Owner decision (CORE-588): «Медиа» IS the existing recording→VideoPractice
 * promotion, re-skinned. So publishing is a two-step, both on endpoints that
 * already exist:
 *
 *   1. `updateArchive`  — persist the метаданные edits onto the room archive
 *      (title/description/cover/visibility). CORE-115 propagates these onto
 *      the promoted VideoPractice, so editing before promoting is enough.
 *   2. `promoteRoomRecordingToVideo` — idempotent promote of the Ready
 *      recording into a VideoPractice that surfaces in the master's Медиа.
 *
 * Снятие с публикации → `unpublishRoomRecording` (hides the Медиа copy, keeps
 * the room + recording + chat). Удаление записи → `deleteRoomRecording`
 * (removes the recording, keeps the room). Both new in CORE-588 / CORE-554.
 *
 * This modal is rendered only inside `useUnifiedLiveEnabled()` branches, so
 * legacy quick-room / live-practice completion flows are byte-identical when
 * the flag is off. Web-first (the whole unified Live-session is an FE-Web
 * epic); cover upload mirrors the wizard StepCover pattern (web file input +
 * native «доступно в веб-версии» note).
 */
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, Modal, ScrollView, Platform, Image, ActivityIndicator, Alert } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { api, getFileUrl } from '../../services/api';
import { friendlyApiError } from '../../services/errorMessages';
import type { RoomResponse, WatchPartyVisibility } from '../../types/auth';
import { useTheme } from '../../context/ThemeContext';
import { RoomRecordingPlayer } from './RoomRecordingPlayer';
import { useRecordingHasVideo } from './useRecordingHasVideo';
import { publishRecordingToMedia } from './publishToMedia';
import { RECORDING_VISIBILITY, VISIBILITY_MEDIA_NOTE } from './recordingAccessCopy';
import { trackLiveSession, LiveSessionEvents } from '../../services/liveSessionAnalytics';
import { fontWeights } from '../../styles/typographyTokens';

interface Props {
  room: RoomResponse;
  onClose: () => void;
  /** Fired after a successful promote — recording is now live in Медиа. */
  onPublished?: (videoPracticeId: number) => void;
  /** Fired after снятие с публикации. */
  onUnpublished?: () => void;
  /** Fired after the recording (not the room) is deleted. */
  onRecordingDeleted?: () => void;
  /**
   * Seed the «опубликовано» state when the caller already knows the recording
   * is in Медиа (e.g. reopened from «Мои комнаты»), so «Снять с публикации»
   * shows without a probe. Defaults to false — the promote endpoint is
   * idempotent, so an over-eager «Опубликовать» is harmless either way.
   */
  initiallyPublished?: boolean;
}

// CORE-1526 (п. 10) — та же видимость и те же слова, что в редакторе архива и
// на странице записи. «Скрыта (черновик)» здесь спорило с архивом, где
// «Черновик» — это готовая, но не опубликованная запись любой видимости.
const VISIBILITY_OPTIONS: ReadonlyArray<{ key: WatchPartyVisibility; label: string; hint: string; icon: React.ComponentProps<typeof Ionicons>['name'] }> = [
  { key: 'Subscribers', icon: 'people-outline', ...RECORDING_VISIBILITY.Subscribers },
  { key: 'Public', icon: 'globe-outline', ...RECORDING_VISIBILITY.Public },
  { key: 'Private', icon: 'eye-off-outline', ...RECORDING_VISIBILITY.Private },
];

type Busy = 'idle' | 'publishing' | 'unpublishing' | 'deleting';

export const PublishToMediaModal: React.FC<Props> = ({
  room,
  onClose,
  onPublished,
  onUnpublished,
  onRecordingDeleted,
  initiallyPublished = false,
}) => {
  const { isDark, colors: tc } = useTheme();
  // CORE-1383 — превью аудиозаписи не притворяется видео.
  const recordingHasVideo = useRecordingHasVideo(typeof room.id === 'number' ? room.id : null, room.recordingState);

  const [title, setTitle] = useState(room.title ?? room.videoPracticeTitle ?? '');
  const [description, setDescription] = useState(room.description ?? '');
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(room.coverImageUrl ?? room.videoPracticeCoverImageUrl ?? null);
  const [visibility, setVisibility] = useState<WatchPartyVisibility>(room.visibility ?? 'Subscribers');

  const [published, setPublished] = useState(initiallyPublished);
  const [busy, setBusy] = useState<Busy>('idle');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = React.useRef<any>(null);

  const recordingReady = room.recordingState === 'Ready' && !!room.playbackUrl;
  const busyAny = busy !== 'idle';

  const onFile = async (file: File) => {
    if (!file.type.startsWith('image/')) { setError('Только изображения'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Файл больше 5 МБ'); return; }
    setError(null);
    setUploadingCover(true);
    try {
      const { url } = await api.uploadRoomCover(file);
      setCoverImageUrl(url);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить обложку');
    } finally {
      setUploadingCover(false);
    }
  };

  const handlePublish = async () => {
    if (busyAny || !recordingReady) return;
    setBusy('publishing');
    setError(null);
    try {
      const res = await publishRecordingToMedia(room.id, { title, description, coverImageUrl, visibility });
      setPublished(true);
      trackLiveSession(LiveSessionEvents.recordingPublished, {
        roomId: room.id,
        videoPracticeId: res.videoPracticeId,
        visibility,
        source: 'publish_modal',
      });
      onPublished?.(res.videoPracticeId);
    } catch (e: any) {
      setError(e?.message || 'Не удалось опубликовать запись. Попробуйте ещё раз.');
      trackLiveSession(LiveSessionEvents.recordingPublishFailed, { roomId: room.id, source: 'publish_modal' });
    } finally {
      setBusy('idle');
    }
  };

  const handleUnpublish = async () => {
    if (busyAny) return;
    setBusy('unpublishing');
    setError(null);
    try {
      await api.unpublishRoomRecording(room.id);
      setPublished(false);
      trackLiveSession(LiveSessionEvents.recordingUnpublished, { roomId: room.id });
      onUnpublished?.();
    } catch (e: any) {
      setError(e?.message || 'Не удалось снять запись с публикации.');
    } finally {
      setBusy('idle');
    }
  };

  const runDeleteRecording = async () => {
    setBusy('deleting');
    setError(null);
    trackLiveSession(LiveSessionEvents.recordingDeleteStarted, { roomId: room.id });
    try {
      await api.deleteRoomRecording(room.id);
      trackLiveSession(LiveSessionEvents.recordingDeleted, { roomId: room.id });
      onRecordingDeleted?.();
      onClose();
    } catch (e: any) {
      // CORE-1257 — `fetchWithTimeout` re-tags a stalled request as
      // `TimeoutError` (see api.ts), so this is the one place that can tell
      // "server said no" apart from "we gave up waiting" for telemetry.
      const timedOut = e?.name === 'TimeoutError';
      setError(`Не удалось удалить запись. ${friendlyApiError(e)}`);
      setBusy('idle');
      trackLiveSession(LiveSessionEvents.recordingDeleteFailed, { roomId: room.id, timedOut, status: e?.status });
    }
  };

  const handleDeleteRecording = () => {
    if (busyAny) return;
    const titleConfirm = 'Удалить запись?';
    const msg = 'Запись Live-сессии будет удалена без возможности восстановления. Сама Live-сессия и история чата сохранятся. Если запись была опубликована в Медиа — она пропадёт из библиотеки.';
    if (Platform.OS === 'web') {
      if ((window as any).confirm?.(`${titleConfirm}\n\n${msg}`)) runDeleteRecording();
    } else {
      Alert.alert(titleConfirm, msg, [
        { text: 'Отмена', style: 'cancel' },
        { text: 'Удалить', style: 'destructive', onPress: runDeleteRecording },
      ]);
    }
  };

  const preview = coverImageUrl ? getFileUrl(coverImageUrl) : null;

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onClose}>
      <View testID="recording-publish-modal" style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 16 } as any}>
        <View style={{ width: '100%', maxWidth: 560, borderRadius: 20, backgroundColor: isDark ? '#15171A' : '#FFFFFF', overflow: 'hidden' } as any}>
          {/* Header */}
          <View style={{ padding: 24, paddingBottom: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderBottomWidth: 1, borderBottomColor: tc.border } as any}>
            <Text style={{ color: tc.textPrimary, fontSize: 18, fontWeight: fontWeights.bold } as any}>Публикация в Медиа</Text>
            <TouchableOpacity testID="recording-publish-close" onPress={onClose} accessibilityLabel="Закрыть" style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6', alignItems: 'center', justifyContent: 'center' }}>
              <Ionicons name="close" size={18} color={tc.textMuted} />
            </TouchableOpacity>
          </View>

          <ScrollView style={{ maxHeight: 560 }}>
            <View style={{ padding: 24, gap: 18 }}>
              {/* Preview — recording player (or its processing / unavailable state) */}
              <RoomRecordingPlayer
                playbackUrl={room.playbackUrl}
                state={room.recordingState}
                coverImageUrl={coverImageUrl ?? room.videoPracticeCoverImageUrl ?? null}
                hasVideo={recordingHasVideo}
                telemetry={typeof room.id === 'number' ? { roomId: room.id, source: 'publish_media', isOwner: true } : undefined}
              />

              {!recordingReady && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#F8F8F6', borderRadius: 12, padding: 14 } as any}>
                  {room.recordingState === 'Failed' ? (
                    <>
                      <Ionicons name="alert-circle-outline" size={20} color="#DC2626" />
                      <Text style={{ color: tc.textPrimary, fontSize: 13, flex: 1 } as any}>Запись не сохранилась — публикация недоступна.</Text>
                    </>
                  ) : room.recordingState === 'None' ? (
                    <>
                      <Ionicons name="videocam-off-outline" size={20} color={tc.textMuted} />
                      <Text style={{ color: tc.textPrimary, fontSize: 13, flex: 1 } as any}>В этой Live-сессии записи нет — публиковать нечего.</Text>
                    </>
                  ) : (
                    <>
                      <ActivityIndicator size="small" color={SOLAR} />
                      <Text style={{ color: tc.textPrimary, fontSize: 13, flex: 1 } as any}>Запись ещё обрабатывается. Публикация станет доступна, когда она будет готова.</Text>
                    </>
                  )}
                </View>
              )}

              {/* Название */}
              <View>
                <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>НАЗВАНИЕ</Text>
                <TextInput
                  testID="recording-publish-title"
                  value={title}
                  onChangeText={setTitle}
                  placeholder="Название записи в Медиа"
                  placeholderTextColor={tc.textMuted}
                  editable={!busyAny}
                  style={{ borderWidth: 1, borderColor: tc.border, borderRadius: 12, padding: 14, color: tc.textPrimary, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#FFFFFF', fontSize: 15 } as any}
                />
              </View>

              {/* Описание */}
              <View>
                <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>ОПИСАНИЕ</Text>
                <TextInput
                  testID="recording-publish-description"
                  value={description}
                  onChangeText={setDescription}
                  multiline
                  placeholder="О чём эта запись?"
                  placeholderTextColor={tc.textMuted}
                  editable={!busyAny}
                  style={{ borderWidth: 1, borderColor: tc.border, borderRadius: 12, padding: 14, color: tc.textPrimary, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#FFFFFF', fontSize: 14, minHeight: 90, textAlignVertical: 'top' } as any}
                />
              </View>

              {/* Обложка */}
              <View>
                <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>ОБЛОЖКА</Text>
                <View style={{ borderWidth: 1, borderColor: tc.border, borderRadius: 12, overflow: 'hidden', aspectRatio: 16 / 9, backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#F3F4F6', alignItems: 'center', justifyContent: 'center' } as any}>
                  {preview ? (
                    <Image source={{ uri: preview }} style={{ width: '100%', height: '100%' } as any} resizeMode="cover" />
                  ) : (
                    <Ionicons name="image-outline" size={36} color={tc.textMuted} />
                  )}
                  {uploadingCover && (
                    <View style={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.35)' } as any}>
                      <ActivityIndicator color="#FFF" />
                    </View>
                  )}
                </View>
                {Platform.OS === 'web'
                  ? React.createElement('input' as any, {
                      ref: inputRef,
                      type: 'file',
                      accept: 'image/*',
                      style: { display: 'none' },
                      onChange: (e: any) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; },
                    })
                  : null}
                <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
                  <TouchableOpacity
                    onPress={() => inputRef.current?.click?.()}
                    disabled={Platform.OS !== 'web' || busyAny}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 100, borderWidth: 1, borderColor: SOLAR, opacity: Platform.OS !== 'web' ? 0.5 : 1, ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : null) } as any}
                  >
                    <Ionicons name="cloud-upload-outline" size={16} color={SOLAR} />
                    <Text style={{ color: SOLAR, fontWeight: '600', fontSize: 13 } as any}>{preview ? 'Заменить' : 'Загрузить'}</Text>
                  </TouchableOpacity>
                  {preview && (
                    <TouchableOpacity
                      onPress={() => setCoverImageUrl(null)}
                      disabled={busyAny}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 100, borderWidth: 1, borderColor: tc.border, ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : null) } as any}
                    >
                      <Ionicons name="trash-outline" size={16} color={tc.textMuted} />
                      <Text style={{ color: tc.textMuted, fontWeight: '600', fontSize: 13 } as any}>Убрать</Text>
                    </TouchableOpacity>
                  )}
                </View>
                {Platform.OS !== 'web' && (
                  <Text style={{ color: tc.textMuted, fontSize: 12, marginTop: 8 } as any}>Загрузка обложки доступна в веб-версии.</Text>
                )}
              </View>

              {/* Видимость */}
              <View>
                <Text style={{ color: tc.textMuted, fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 1, marginBottom: 8 } as any}>КТО ВИДИТ ЗАПИСЬ</Text>
                <View style={{ gap: 8 }}>
                  {VISIBILITY_OPTIONS.map(opt => {
                    const active = visibility === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        testID={`recording-publish-visibility-${opt.key.toLowerCase()}`}
                        onPress={() => setVisibility(opt.key)}
                        disabled={busyAny}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: active ? SOLAR : tc.border, backgroundColor: active ? (isDark ? 'rgba(233, 130, 69,0.1)' : '#FFF5EE') : 'transparent' } as any}
                      >
                        <Ionicons name={active ? 'radio-button-on' : 'radio-button-off'} size={18} color={active ? SOLAR : tc.textMuted} />
                        <View style={{ flex: 1, gap: 2 } as any}>
                          <Text style={{ color: tc.textPrimary, fontSize: 14 } as any}>{opt.label}</Text>
                          <Text style={{ color: tc.textMuted, fontSize: 12 } as any}>{opt.hint}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                <Text style={{ color: tc.textMuted, fontSize: 12, marginTop: 8 } as any}>{VISIBILITY_MEDIA_NOTE}</Text>
              </View>

              {published && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: isDark ? 'rgba(34,197,94,0.10)' : '#ECFDF3', borderRadius: 12, padding: 12 } as any}>
                  <Ionicons name="checkmark-circle" size={18} color="#16A34A" />
                  <Text style={{ color: '#16A34A', fontSize: 13, fontWeight: '600', flex: 1 } as any}>Запись опубликована в Медиа. Она доступна в вашей библиотеке практик.</Text>
                </View>
              )}

              {error && <Text style={{ color: '#DC2626', fontSize: 13 } as any}>{error}</Text>}
            </View>
          </ScrollView>

          {/* Footer — delete-recording (left) · publish/unpublish (right) */}
          <View style={{ padding: 20, paddingTop: 14, flexDirection: 'row', gap: 12, justifyContent: 'space-between', alignItems: 'center', borderTopWidth: 1, borderTopColor: tc.border } as any}>
            <TouchableOpacity
              testID="recording-delete-asset"
              onPress={handleDeleteRecording}
              disabled={busyAny || room.recordingState === 'None'}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 16, paddingVertical: 12, borderRadius: 100, borderWidth: 1, borderColor: 'rgba(239,68,68,0.4)', opacity: (busyAny || room.recordingState === 'None') ? 0.5 : 1 } as any}
            >
              {busy === 'deleting' ? <ActivityIndicator size="small" color="#EF4444" /> : <Ionicons name="trash-outline" size={14} color="#EF4444" />}
              <Text style={{ color: '#EF4444', fontSize: 13, fontWeight: '600' } as any}>Удалить запись</Text>
            </TouchableOpacity>

            {published ? (
              <TouchableOpacity
                testID="recording-unpublish-media"
                onPress={handleUnpublish}
                disabled={busyAny}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 22, paddingVertical: 12, borderRadius: 100, borderWidth: 1, borderColor: tc.border, opacity: busyAny ? 0.6 : 1 } as any}
              >
                {busy === 'unpublishing' ? <ActivityIndicator size="small" color={tc.textPrimary} /> : <Ionicons name="eye-off-outline" size={16} color={tc.textPrimary} />}
                <Text style={{ color: tc.textPrimary, fontSize: 13, fontWeight: fontWeights.bold } as any}>Снять с публикации</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                testID="recording-publish-media"
                onPress={handlePublish}
                disabled={busyAny || !recordingReady}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 100, backgroundColor: SOLAR, opacity: (busyAny || !recordingReady) ? 0.5 : 1 } as any}
              >
                {busy === 'publishing' ? <ActivityIndicator size="small" color="#FFF" /> : <Ionicons name="cloud-upload-outline" size={16} color="#FFF" />}
                <Text style={{ color: '#FFF', fontSize: 13, fontWeight: fontWeights.bold, letterSpacing: 0.3 } as any}>Опубликовать в Медиа</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const SOLAR = '#E98245';

export default PublishToMediaModal;
